import React, { useState, useCallback, useRef } from 'react';
import { Plus, Search, X, Check, Edit3, Trash2, ShieldCheck } from 'lucide-react';
import Breadcrumb from '../../components/UI/Breadcrumb/Breadcrumb';
import './SubscriptionAdmin.css';

const SubscriptionAdmin = () => {
  const breadcrumbItems = [
    { label: '首页', path: '/dashboard' },
    { label: '订阅管理', path: null },
  ];

  const [phone, setPhone] = useState('');
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [toast, setToast] = useState(null);

  // Add user modal state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserPhone, setAddUserPhone] = useState('');
  const [addingUser, setAddingUser] = useState(false);

  // Add subscription modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ planId: '', startAt: '', endAt: '' });
  const [addTargetUser, setAddTargetUser] = useState(null);
  const [adding, setAdding] = useState(false);

  // Edit subscription modal state
  const [editItem, setEditItem] = useState(null);
  const [editEndAt, setEditEndAt] = useState('');
  const [editing, setEditing] = useState(false);

  // Delete confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const confirmTimerRef = useRef(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  // Format date for input: "2025/01/01" -> "2025-01-01"
  const toInputDate = (str) => {
    if (!str) return '';
    return str.replace(/\//g, '-');
  };

  // Format date for API: "2025-01-01" -> "2025/01/01"
  const toApiDate = (str) => {
    if (!str) return '';
    return str.replace(/-/g, '/');
  };

  const isValidMainlandPhone = (value) => /^1\d{10}$/.test(value);

  const handleSearch = useCallback(async () => {
    if (!phone.trim()) {
      showToast('请输入手机号', 'error');
      return;
    }
    try {
      setLoading(true);
      setSearched(true);
      const searchPayload = { phone: phone.trim() };
      console.log('[SubscriptionAdmin] /api/admin/subscription/search request:', searchPayload);
      const response = await fetch('/api/admin/subscription/search', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchPayload),
      });
      const data = await response.json();
      console.log('[SubscriptionAdmin] /api/admin/subscription/search response:', data);
      if (data.code === 0 && Array.isArray(data.data)) {
        const normalizedUsers = data.data.map(user => ({
          uid: user.uid,
          phone: user.phone,
          name: user.name,
          benefits: Array.isArray(user.benefits) ? user.benefits : [],
        }));
        setUsers(normalizedUsers);
      } else {
        setUsers([]);
      }
    } catch {
      setUsers([]);
      showToast('查询失败，请稍后重试', 'error');
    } finally {
      setLoading(false);
    }
  }, [phone, showToast]);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/plan/get', {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();
      if (data.code === 0 && Array.isArray(data.data)) {
        setPlans(data.data);
      }
    } catch {
      showToast('获取套餐列表失败', 'error');
    }
  }, [showToast]);

  const handleOpenAddUser = useCallback(() => {
    setAddUserPhone(phone.trim());
    setShowAddUserModal(true);
  }, [phone]);

  const handleAddUserSubmit = useCallback(async () => {
    const targetPhone = addUserPhone.trim();
    if (!targetPhone) {
      showToast('请输入手机号', 'error');
      return;
    }
    if (!isValidMainlandPhone(targetPhone)) {
      showToast('手机号格式不正确，请输入11位手机号', 'error');
      return;
    }
    try {
      setAddingUser(true);
      const response = await fetch('/api/admin/user/add', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: targetPhone }),
      });
      const data = await response.json();
      if (data.code === 0) {
        showToast('添加用户成功');
        setShowAddUserModal(false);
        setPhone(targetPhone);
        if (phone.trim() === targetPhone) {
          handleSearch();
        }
      } else {
        showToast(data.msg || '添加用户失败', 'error');
      }
    } catch {
      showToast('网络错误', 'error');
    } finally {
      setAddingUser(false);
    }
  }, [addUserPhone, phone, handleSearch, showToast]);

  const handleOpenAdd = useCallback((user) => {
    fetchPlans();
    setAddTargetUser(user);
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const end = new Date(today);
    end.setMonth(end.getMonth() + 1);
    const eyyyy = end.getFullYear();
    const emm = String(end.getMonth() + 1).padStart(2, '0');
    const edd = String(end.getDate()).padStart(2, '0');

    setAddForm({
      planId: '',
      startAt: `${yyyy}-${mm}-${dd}`,
      endAt: `${eyyyy}-${emm}-${edd}`,
    });
    setShowAddModal(true);
  }, [fetchPlans]);

  const handleAddSubmit = useCallback(async () => {
    if (!addForm.planId || !addForm.startAt || !addForm.endAt) {
      showToast('请填写完整信息', 'error');
      return;
    }
    if (!addTargetUser?.uid) {
      showToast('请先选择用户', 'error');
      return;
    }
    try {
      setAdding(true);
      const response = await fetch('/api/admin/subscription/add', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(addForm.planId, 10),
          uid: addTargetUser.uid,
          startAt: toApiDate(addForm.startAt),
          endAt: toApiDate(addForm.endAt),
        }),
      });
      const data = await response.json();
      if (data.code === 0) {
        showToast('添加成功');
        setShowAddModal(false);
        setAddTargetUser(null);
        handleSearch();
      } else {
        showToast(data.msg || '添加失败', 'error');
      }
    } catch {
      showToast('网络错误', 'error');
    } finally {
      setAdding(false);
    }
  }, [addForm, addTargetUser, handleSearch, showToast]);

  const handleOpenEdit = useCallback((item) => {
    setEditItem(item);
    setEditEndAt(toInputDate(item.endAt));
  }, []);

  const handleEditSubmit = useCallback(async () => {
    if (!editEndAt) {
      showToast('请选择到期时间', 'error');
      return;
    }
    try {
      setEditing(true);
      const response = await fetch('/api/admin/subscription/update', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: editItem.id,
          uid: editItem.uid,
          endAt: toApiDate(editEndAt),
        }),
      });
      const data = await response.json();
      if (data.code === 0) {
        showToast('修改成功');
        setEditItem(null);
        handleSearch();
      } else {
        showToast(data.msg || '修改失败', 'error');
      }
    } catch {
      showToast('网络错误', 'error');
    } finally {
      setEditing(false);
    }
  }, [editItem, editEndAt, handleSearch, showToast]);

  const handleTerminate = useCallback(async (item) => {
    try {
      const response = await fetch('/api/admin/subscription/update', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: item.id,
          uid: item.uid,
          status: 4,
        }),
      });
      const data = await response.json();
      if (data.code === 0) {
        showToast('已终止订阅');
        setConfirmDeleteId(null);
        handleSearch();
      } else {
        showToast(data.msg || '操作失败', 'error');
      }
    } catch {
      showToast('网络错误', 'error');
    }
  }, [handleSearch, showToast]);

  const handleDeleteClick = useCallback((id) => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    setConfirmDeleteId(id);
    confirmTimerRef.current = setTimeout(() => setConfirmDeleteId(null), 3000);
  }, []);

  const getTypeBadgeClass = (type) => {
    switch (type) {
      case '包月': return 'sa-badge sa-badge--monthly';
      case '计时': return 'sa-badge sa-badge--timed';
      case '积分': return 'sa-badge sa-badge--points';
      default: return 'sa-badge';
    }
  };

  return (
    <div className="sa-page">
      <Breadcrumb items={breadcrumbItems} />

      {/* Page header */}
      <div className="sa-header">
        <div className="sa-header-text">
          <h1 className="sa-title">订阅管理</h1>
          <p className="sa-desc">管理用户订阅权益（仅管理员可见）</p>
        </div>
      </div>

      {/* Search card */}
      <div className="sa-card">
        <div className="sa-search-row">
          <div className="sa-search-input-wrap">
            <Search size={16} className="sa-search-icon" />
            <input
              className="sa-search-input"
              type="text"
              placeholder="输入用户手机号查询订阅"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button className="sa-btn sa-btn--primary" onClick={handleSearch} disabled={loading}>
            {loading ? '查询中…' : '查询'}
          </button>
          <button className="sa-btn sa-btn--accent" onClick={handleOpenAddUser} disabled={addingUser}>
            <Plus size={16} />
            添加用户
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="sa-card" style={{ marginTop: 16 }}>
        {loading ? (
          <div className="sa-loading">
            <div className="sa-spinner" />
            <span>加载中…</span>
          </div>
        ) : searched && users.length === 0 ? (
          <div className="sa-empty">
            <div className="sa-empty-icon"><ShieldCheck size={28} /></div>
            <p className="sa-empty-title">未找到用户</p>
            <p className="sa-empty-hint">请调整手机号关键字后重试</p>
          </div>
        ) : !searched ? (
          <div className="sa-empty">
            <div className="sa-empty-icon"><Search size={28} /></div>
            <p className="sa-empty-title">请输入手机号查询</p>
            <p className="sa-empty-hint">输入用户手机号以查看其订阅信息</p>
          </div>
        ) : (
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>手机号</th>
                  <th>ID</th>
                  <th>类型</th>
                  <th>开始时间</th>
                  <th>到期时间</th>
                  <th>总量</th>
                  <th>已用</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.flatMap(user => {
                  if (!user.benefits.length) {
                    return (
                      <tr key={`${user.uid}-no-benefit`}>
                        <td>{user.phone || '-'}</td>
                        <td className="sa-td-id">-</td>
                        <td>-</td>
                        <td className="sa-td-date">-</td>
                        <td className="sa-td-date">-</td>
                        <td>-</td>
                        <td>-</td>
                        <td className="sa-td-actions">
                          <button className="sa-btn sa-btn--accent" onClick={() => handleOpenAdd(user)}>
                            <Plus size={16} />
                            添加订阅
                          </button>
                        </td>
                      </tr>
                    );
                  }

                  return user.benefits.map(benefit => {
                    const item = { ...benefit, uid: user.uid, phone: user.phone };
                    return (
                      <tr key={`${user.uid}-${benefit.id}`}>
                        <td>{user.phone || '-'}</td>
                        <td className="sa-td-id">{benefit.id}</td>
                        <td>
                          <span className={getTypeBadgeClass(benefit.type)}>{benefit.type}</span>
                        </td>
                        <td className="sa-td-date">{benefit.startAt}</td>
                        <td className="sa-td-date">{benefit.endAt}</td>
                        <td>{benefit.total}</td>
                        <td>{benefit.used}</td>
                        <td className="sa-td-actions">
                          <button className="sa-btn sa-btn--accent" onClick={() => handleOpenAdd(user)}>
                            <Plus size={16} />
                            添加订阅
                          </button>
                          <button className="sa-edit-btn" onClick={() => handleOpenEdit(item)}>
                            <Edit3 size={14} />
                            修改
                          </button>
                          {confirmDeleteId === benefit.id ? (
                            <span className="sa-confirm-row">
                              <button className="sa-btn-danger-confirm" onClick={() => handleTerminate(item)}>
                                确认终止
                              </button>
                              <button className="sa-btn-cancel" onClick={() => setConfirmDeleteId(null)}>
                                取消
                              </button>
                            </span>
                          ) : (
                            <button className="sa-delete-btn" onClick={() => handleDeleteClick(benefit.id)}>
                              <Trash2 size={14} />
                              终止
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add subscription modal */}
      {showAddUserModal && (
        <div className="sa-modal-overlay" onClick={() => setShowAddUserModal(false)}>
          <div className="sa-modal" onClick={e => e.stopPropagation()}>
            <div className="sa-modal-header">
              <span className="sa-modal-title">添加用户</span>
              <button className="sa-modal-close" onClick={() => setShowAddUserModal(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="sa-modal-body">
              <div className="sa-form-group">
                <label className="sa-label">手机号 <span className="sa-required">*</span></label>
                <input
                  className="sa-input"
                  type="text"
                  placeholder="请输入手机号"
                  value={addUserPhone}
                  onChange={e => setAddUserPhone(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddUserSubmit()}
                />
              </div>
            </div>
            <div className="sa-modal-footer">
              <button className="sa-btn sa-btn--ghost" onClick={() => setShowAddUserModal(false)}>取消</button>
              <button
                className="sa-btn sa-btn--primary"
                onClick={handleAddUserSubmit}
                disabled={addingUser || !addUserPhone.trim()}
              >
                {addingUser ? '添加中…' : '确认添加'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add subscription modal */}
      {showAddModal && (
        <div className="sa-modal-overlay" onClick={() => {
          setShowAddModal(false);
          setAddTargetUser(null);
        }}>
          <div className="sa-modal" onClick={e => e.stopPropagation()}>
            <div className="sa-modal-header">
              <span className="sa-modal-title">添加订阅（{addTargetUser?.phone || '-'}）</span>
              <button className="sa-modal-close" onClick={() => {
                setShowAddModal(false);
                setAddTargetUser(null);
              }}>
                <X size={16} />
              </button>
            </div>
            <div className="sa-modal-body">
              <div className="sa-form-group">
                <label className="sa-label">选择套餐 <span className="sa-required">*</span></label>
                <select
                  className="sa-select"
                  value={addForm.planId}
                  onChange={e => setAddForm(f => ({ ...f, planId: e.target.value }))}
                >
                  <option value="">请选择套餐</option>
                  {plans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      [{plan.type}] {plan.name} ({plan.duration})
                    </option>
                  ))}
                </select>
              </div>
              <div className="sa-form-row-2col">
                <div className="sa-form-group">
                  <label className="sa-label">开始时间 <span className="sa-required">*</span></label>
                  <input
                    className="sa-input"
                    type="date"
                    value={addForm.startAt}
                    onChange={e => setAddForm(f => ({ ...f, startAt: e.target.value }))}
                  />
                </div>
                <div className="sa-form-group">
                  <label className="sa-label">到期时间 <span className="sa-required">*</span></label>
                  <input
                    className="sa-input"
                    type="date"
                    value={addForm.endAt}
                    onChange={e => setAddForm(f => ({ ...f, endAt: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="sa-modal-footer">
              <button className="sa-btn sa-btn--ghost" onClick={() => {
                setShowAddModal(false);
                setAddTargetUser(null);
              }}>取消</button>
              <button
                className="sa-btn sa-btn--primary"
                onClick={handleAddSubmit}
                disabled={adding || !addForm.planId || !addForm.startAt || !addForm.endAt}
              >
                {adding ? '添加中…' : '确认添加'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit subscription modal */}
      {editItem && (
        <div className="sa-modal-overlay" onClick={() => setEditItem(null)}>
          <div className="sa-modal" onClick={e => e.stopPropagation()}>
            <div className="sa-modal-header">
              <span className="sa-modal-title">修改订阅到期时间</span>
              <button className="sa-modal-close" onClick={() => setEditItem(null)}>
                <X size={16} />
              </button>
            </div>
            <div className="sa-modal-body">
              <div className="sa-form-group">
                <label className="sa-label">当前信息</label>
                <div className="sa-info-row">
                  <span className={getTypeBadgeClass(editItem.type)}>{editItem.type}</span>
                  <span className="sa-info-text">
                    {editItem.startAt} ~ {editItem.endAt} | 总量: {editItem.total} | 已用: {editItem.used}
                  </span>
                </div>
              </div>
              <div className="sa-form-group">
                <label className="sa-label">新的到期时间 <span className="sa-required">*</span></label>
                <input
                  className="sa-input"
                  type="date"
                  value={editEndAt}
                  onChange={e => setEditEndAt(e.target.value)}
                />
              </div>
            </div>
            <div className="sa-modal-footer">
              <button className="sa-btn sa-btn--ghost" onClick={() => setEditItem(null)}>取消</button>
              <button
                className="sa-btn sa-btn--primary"
                onClick={handleEditSubmit}
                disabled={editing || !editEndAt}
              >
                {editing ? '修改中…' : '确认修改'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`sa-toast sa-toast--${toast.type}`}>
          {toast.type === 'success' ? <Check size={15} /> : <X size={15} />}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default SubscriptionAdmin;
